import React, { useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../../theme/colors';
import { Text } from '../common/Text/Text';
import { MailIcon } from '../../../icons';
import { usePushEvents } from '../../../../hooks/PushEvents';
import { viewEvent } from '../../../../hooks/PushEvents/clevertapEvents';

const viewEventPayload = {
    location: 'GiftSummary',
    name: 'EmailVerificationError',
};

interface EmailVerificationErrorProps {
    resendEmailLoading: boolean;
    resendEmailSuccess: boolean;
    resendEmailError: string | undefined;
    email?: string;
    onResend: () => void;
}

const StyledWrapper = styled.div`
    margin-top: 16px;
    padding: 14px 16px;
    border: 1px solid ${colors.utilityError};
    background-color: ${colors.utilityErrorLightColor};
    border-radius: 12px;
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .icon-wrapper {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: ${colors.white};
        display: flex;
        align-items: center;
        justify-content: center;
        svg path {
            fill: ${colors.utilityError};
        }
    }

    .content {
        flex: 1;
        min-width: 0;
    }

    .error-message {
        display: block;
        padding-top: 0;
        text-align: left;
        line-height: 20px;
    }

    .error-title {
        display: block;
        font-size: 16px;
        line-height: 22px;
        font-weight: 700;
        margin-bottom: 6px;
        color: ${colors.utilityError};
    }

    .email-address {
        font-weight: 600;
        word-break: break-all;
    }

    .resend-line {
        display: block;
        margin-top: 10px;
        color: ${colors.grayscaleGray700};
    }

    .ghost-button {
        background: none;
        border: none;
        padding: 0;
        margin-left: 6px;
        font-weight: 700;
        color: ${colors.primaryGuidingViolet};
        cursor: pointer;
        font: inherit;
        text-decoration: underline;
        transition: color 0.15s ease;
    }

    .ghost-button:hover:not(:disabled) {
        color: ${colors.buttonsHoverViolet};
    }

    .ghost-button:disabled {
        cursor: not-allowed;
        color: ${colors.grayscaleGray500};
        text-decoration: none;
    }
`;

const EmailVerificationError: React.FC<EmailVerificationErrorProps> = ({
    resendEmailLoading,
    resendEmailSuccess,
    resendEmailError,
    email,
    onResend,
}) => {
    const { pushEvent } = usePushEvents();

    useEffect(() => {
        pushEvent(viewEvent, viewEventPayload);
        // run only once when component is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buttonText = resendEmailLoading
        ? 'Sending...'
        : resendEmailSuccess
          ? 'Email sent'
          : resendEmailError
            ? 'Retry'
            : 'Resend';

    return (
        <StyledWrapper>
            <div className="icon-wrapper">
                <MailIcon />
            </div>
            <div className="content">
                {resendEmailSuccess ? (
                    <Text
                        color={'utilityError'}
                        className={'error-message'}
                    >
                        <span className="error-title">
                            Verification email sent
                        </span>
                        Please check your inbox and open the link to continue.
                    </Text>
                ) : (
                    <Text
                        color={'utilityError'}
                        className={'error-message'}
                    >
                        <span className="error-title">
                            Please verify your email to continue
                        </span>
                        We sent a verification link to{' '}
                        <span className="email-address">
                            {email ?? 'your email'}
                        </span>
                        . Open the link in the email, then return to this page
                        and click Give to complete your donation.
                        <span className="resend-line">
                            Didn't get the email?
                            <button
                                type="button"
                                className="ghost-button"
                                onClick={onResend}
                                disabled={
                                    resendEmailLoading || resendEmailSuccess
                                }
                            >
                                {buttonText}
                            </button>
                        </span>
                    </Text>
                )}
            </div>
        </StyledWrapper>
    );
};

export default EmailVerificationError;
